import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import axios, { AxiosError, AxiosResponse } from 'axios';
import Event from '../../lib/EventEmitter';
import { useFetchReducer } from '../../hooks/reducers/useFetchReducer';
import { delayAsync } from '../../lib/delayAsync';
import { SonResponseError } from './types/SonResponseError';
import { AnyParameter, SonForm } from './types/SonForm';
import { useHtmlValidId } from '../../hooks/useHtmlValidId';
import { formParamsUtils } from './utils/formParamsUtils';
import { removeControlCharacters } from '../StringUtils';
import { useLocation } from 'react-router';
import { convertAttachmentLinkHtmlString } from '../../components/molecules/Form/components/Editor/utils/tinyUtils';

export type UseForm2Props<TForm extends SonForm<AnyParameter>> = {
  form: TForm;
  activateLeavingGuard?: boolean;
  onSuccess?: (response: AxiosResponse, formData?: FormData) => void;
  onError?: (reason?: any) => void;
};

/**
 * Returns a new sanitized FormData object.
 * Only values of type string should be cleaned.
 * See SON-12964
 */
function sanitizeFormData(data: FormData): FormData | undefined {
  const cleanedFormData: FormData = new FormData();
  // data any because TS doesn't know FormData (MDN) here
  const dataEntries = Array.from((data as any).entries());
  if (dataEntries.length === 0) {
    return undefined;
  }

  const hasAttachmentLink = (value: string): boolean => {
    if (!value || !/<a\b/.test(value)) {
      return false;
    }

    // Check for <a> Tag with proper attributes - representing our file links from Tiny
    const attachmentPattern =
      /<a[^>]*(?:data-link-type\s*=\s*(?:"attachment"|'attachment')|data-breed\s*=\s*(?:"attachment"|'attachment'))[^>]*>/i;

    return attachmentPattern.test(value);
  };

  const isValidFile = (file: File): boolean => file.name !== '' && file.size !== 0;

  dataEntries.forEach((entry: any) => {
    const [key, value] = entry;

    if (typeof value === 'string') {
      let cleanedValue = removeControlCharacters(value) as string;

      if (hasAttachmentLink(cleanedValue)) {
        cleanedValue = convertAttachmentLinkHtmlString(cleanedValue);
      }

      cleanedFormData.append(key, cleanedValue);
    } else if (isValidFile(value)) {
      // sanitation for File type values
      cleanedFormData.append(key, value);
    }
  });
  return cleanedFormData;
}

/**
 * This is a simple HTML based Form helper based on the requirements of our old junky Form.jsx
 *
 * goals of this:
 *  - no formModel or observable magic behind the scene -> html is the model
 *  - payload collection from HTML, no collection via JS
 *  - validation is done with FormHTMLInstance methods
 */
export function useForm<TForm extends SonForm<AnyParameter>, TResp = any>({
  form,
  activateLeavingGuard = false,
  onSuccess = undefined,
  onError = undefined,
}: UseForm2Props<TForm>) {
  const location = useLocation();
  const { state, receivedError, receivedResponse, reset, startRequest } = useFetchReducer<
    AxiosResponse<TResp>,
    AxiosError<SonResponseError>
  >();
  const idForLeavingGuard = useHtmlValidId();
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const ref = useRef<HTMLFormElement>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nativeEvent = event.nativeEvent as typeof event.nativeEvent & { submitter: HTMLButtonElement };
    let currentAction = nativeEvent?.submitter?.formAction;

    if (!currentAction || new URL(currentAction).pathname === location.pathname) {
      currentAction = event.currentTarget.action;
    }

    if (currentAction) {
      const formElement = event.target as HTMLFormElement;
      const submitter = (event.nativeEvent as SubmitEvent).submitter;
      const formData = sanitizeFormData(new FormData(formElement, submitter));
      const fileInputHasFiles = Array.from(formElement.querySelectorAll('input[type="file"]')).some(
        input => ((input as HTMLInputElement).files?.length ?? 0) > 0
      );
      const formDataHasFile = (formData?.values().some(entry => entry instanceof File) ?? false) || fileInputHasFiles;
      // When the form contains files, let axios set the Content-Type header (multipart/form-data with the
      // correct boundary). Setting it manually without a boundary breaks the multipart parsing on the server.
      // When there are no files, use url encoding to be conform with browser behaviour when no javascript is available.
      const responsePromise = formDataHasFile
        ? axios.post(currentAction, formData)
        : axios.post(currentAction, new URLSearchParams(formData as any), {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          });

      startRequest(currentAction);

      const [axiosResponse] = await Promise.allSettled([responsePromise, delayAsync(1000)]);

      if (axiosResponse.status === 'fulfilled') {
        receivedResponse(axiosResponse.value, currentAction);
        setHasChanges(false);

        if (onSuccess) {
          onSuccess(axiosResponse.value);
        }
      } else {
        receivedError(axiosResponse.reason, currentAction);

        if (onError) {
          onError(axiosResponse.reason);
        }
      }
    }
  };

  const resetForm = useCallback(() => {
    if (ref && ref.current) {
      ref.current.reset();
      setHasChanges(false);
      reset();
    }
  }, [reset]);

  /**
   * onChange method of Form, which fires if any controlled input of this form changes value.
   */
  const onChange = () => {
    if (state.type === 'error') {
      reset();
    }

    if (!hasChanges) {
      setHasChanges(true);
    }
  };

  // reset the form if the form url changes
  useEffect(() => {
    resetForm();
  }, [form.url, reset, resetForm]);

  useEffect(() => {
    if (activateLeavingGuard) {
      Event.emit(Event.prompt.addToQueue, { id: idForLeavingGuard, isDirty: () => hasChanges });
    }
    return () => {
      Event.emit(Event.prompt.removeFromQueue, idForLeavingGuard);
    };
  }, [activateLeavingGuard, hasChanges, idForLeavingGuard]);

  // use this to init form: <form {...formProps}>
  const formProps = {
    onSubmit,
    onChange,
    ref,
    action: form.url,
    method: form.method,
  };

  return {
    formProps,
    resetForm,
    hasChanges,
    fetchState: state,
    resetFetchState: reset,
    params: formParamsUtils<TForm['params'][number]>(form),
  };
}
